import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../primitives/card.js';
import { PageHeader } from '../../primitives/page-header.js';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../primitives/collapsible.js';
import { ErrorState, ForbiddenState } from '../../primitives/states.js';
import { Skeleton } from '../../primitives/skeleton.js';
import { cn } from '../../lib/cn.js';
import { formatValue } from '../format.js';
import type { DetailField, DetailSection, ResourceDetailSpec } from '../types.js';

function Field<TItem>({ field, row }: { field: DetailField<TItem>; row: TItem }) {
  const body = field.render
    ? field.render(row)
    : formatValue(
        field.accessor ? field.accessor(row) : (row as Record<string, unknown>)[field.id],
      );

  return (
    <div className="grid gap-0.5">
      <dt className="text-label uppercase tracking-[0.08em] text-faint">{field.label}</dt>
      <dd className="text-body text-ink">{body}</dd>
    </div>
  );
}

function Fields<TItem>({ section, row }: { section: DetailSection<TItem>; row: TItem }) {
  if (section.render) return <>{section.render(row)}</>;

  const after = section.renderAfter?.(row);

  return (
    <div className="grid gap-[var(--card-gap)]">
      <dl
        className={cn(
          'grid gap-4',
          (section.columns ?? 2) === 2 && 'sm:grid-cols-2',
          section.columns === 3 && 'sm:grid-cols-3',
          section.columns === 4 && 'sm:grid-cols-2 lg:grid-cols-4',
        )}
      >
        {section.fields?.map((field) => (
          <Field key={field.id} field={field} row={row} />
        ))}
      </dl>
      {after}
    </div>
  );
}

function Section<TItem>({ section, row }: { section: DetailSection<TItem>; row: TItem }) {
  // A collapsible section without a title has no handle to click, so it is
  // rendered open rather than made unreachable.
  if (section.collapsible && section.title) {
    return (
      <Card>
        <Collapsible defaultOpen={!(section.defaultCollapsed ?? false)}>
          {/* `Card` already carries the vertical padding, so the trigger only
              adds the horizontal one and stays flush with a normal header. */}
          <CollapsibleTrigger className="group flex w-full items-center gap-2 px-[var(--card-gap)] text-left">
            <CardTitle>{section.title}</CardTitle>
            <ChevronDown className="ml-auto size-4 text-muted transition-transform group-data-[state=open]:rotate-180" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-[var(--card-gap)]">
              <Fields section={section} row={row} />
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    );
  }

  return (
    <Card>
      {section.title && (
        <CardHeader>
          <CardTitle>{section.title}</CardTitle>
        </CardHeader>
      )}
      <CardContent>
        <Fields section={section} row={row} />
      </CardContent>
    </Card>
  );
}

export interface ResourceDetailProps<TItem> {
  spec: ResourceDetailSpec<TItem>;
  item: TItem | undefined;
  isPending?: boolean;
  error?: unknown;
  forbidden?: boolean;
  onRetry?: () => void;
  /** Rendered top-right — edit and delete buttons, usually. */
  actions?: React.ReactNode;
  className?: string;
}

export function ResourceDetail<TItem>({
  spec,
  item,
  isPending,
  error,
  forbidden,
  onRetry,
  actions,
  className,
}: ResourceDetailProps<TItem>) {
  if (forbidden) return <ForbiddenState />;
  if (error) return <ErrorState onRetry={onRetry} />;

  if (isPending || !item) {
    return (
      <div className="grid gap-[var(--card-gap)]" aria-busy>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <section
      data-slot="resource-detail"
      className={cn('flex flex-col gap-[var(--card-gap)]', className)}
    >
      <PageHeader
        title={spec.title?.(item) ?? ''}
        actions={
          (spec.actions || actions) && (
            <>
              {spec.actions?.(item)}
              {actions}
            </>
          )
        }
      />

      {spec.sections.map((section) => (
        <Section key={section.id} section={section} row={item} />
      ))}

      {spec.after?.(item)}
    </section>
  );
}
